import React from "react";

import {
  faBars,
  faShare,
  faAddressCard,
  faChartColumn,
  faCogs,
  faFileChartLine,
  faGavel,
  faHeadset,
  faHouse,
  faInbox,
  faPlus,
  faWallet,
  faTruck,
  faClose,
} from "@fortawesome/pro-regular-svg-icons";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { SaleyardPermissions } from "constants/permissions";
import { userTypes } from "constants/users";

import {
  getAnalyticsRoute,
  getDashboardRoute,
  getInboxRoute,
  getManageBusinessRoute,
  getMoneyTableRoute,
  getReportJobList,
  getSaleyardAuctionRoute,
  getSettingsRoute,
  openConsignToOtherSaleModal,
  openHelpAndSupport,
  getLoadsRoute,
} from "lib/navigation";

import {
  getCurrentSaleyard,
  getCurrentUser,
  getHasEnteredNlisCreds,
  getIsLivestockAgent,
  getIsScaleOperator,
  getSaleyards,
  selectCanSeeAgencyGroups,
  selectCanSeeOtherDeployments,
  selectIsUserOfType,
  getIsTransporter,
} from "selectors";

import { useHasBillingPermission, useIsMobile } from "hooks";
import { useOpenCreateSaleModal } from "hooks/useOpenCreateSaleModal";
import {
  useHasEmailListPermission,
  useHasMultiSaleReportPermission,
  useHasSaleyardPermission,
} from "hooks/useHasPermission";

import { SidebarListItem } from "./SidebarListItem";

const NLIS_AWARE_USER_TYPES = [
  userTypes.SCALE_OPERATOR,
  userTypes.SALEYARD_ADMIN,
  userTypes.LIVESTOCK_AGENT,
];

interface DefaultSidebarContentProps {
  closeSidebar: () => void;
  toggleSidebar?: () => void;
  isOpen?: boolean;
}

export function DefaultSidebarContent(props: DefaultSidebarContentProps) {
  const { closeSidebar, toggleSidebar, isOpen } = props;
  const history = useHistory();
  const openCreateSaleModal = useOpenCreateSaleModal();

  // Try and pull the current saleyard out
  const currentSaleyard = useSelector(getCurrentSaleyard);
  // Also pull out all of our saleyards
  const saleyards = useSelector(getSaleyards);
  // Set the saleyard to be the current name if it exits
  // If it doesn't exist, but we do have other saleyards belonging to us then just use the first one for now, until we navigate into a sale.
  // This allows tabs like businesses/contacts/settings to work when on urls without a saleyard like the sale dashboard.
  const saleyard = (currentSaleyard || Object.values(saleyards)?.[0])?.name;

  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isTransporter = useSelector(getIsTransporter);

  const showSaleDashboard = !isTransporter;

  const areNlisCredentialsRequired = useSelector(getIsScaleOperator);
  const areNlisCredentialsProvided = useSelector(getHasEnteredNlisCreds);
  const canSeeOtherDeployments = useSelector(selectCanSeeOtherDeployments);
  const canSeeAgencyGroups = useSelector(selectCanSeeAgencyGroups);

  const canViewEnvds = useHasSaleyardPermission(
    SaleyardPermissions.featureEnvds,
  );

  const canSeeMultiSaleReports = useHasMultiSaleReportPermission();
  const canSeeBilling = useHasBillingPermission();

  const canViewEmail = useHasEmailListPermission();

  const showInbox = canViewEnvds || canViewEmail;

  const nlisUpdateNeeded =
    areNlisCredentialsRequired && !areNlisCredentialsProvided;
  const allowSales = !nlisUpdateNeeded && !isTransporter;

  const isNLISAwareUser = useSelector(
    selectIsUserOfType(NLIS_AWARE_USER_TYPES),
  );

  const user = useSelector(getCurrentUser);
  const isMobile = useIsMobile();
  const allowAddSale = !nlisUpdateNeeded && isNLISAwareUser && !isTransporter;

  const allowSetNLISCredentials = isNLISAwareUser;

  const allowConsignToOtherSale =
    canSeeOtherDeployments && canSeeAgencyGroups && isLivestockAgent;

  const canAccessAnalytics = user.allow_analytics;

  const goToBusinesses = () => {
    closeSidebar();
    history.push(getManageBusinessRoute());
  };

  const goToSales = () => {
    closeSidebar();
    history.push(getSaleyardAuctionRoute(saleyard));
  };

  const goToSaleDashboard = () => {
    closeSidebar();
    history.push(getDashboardRoute());
  };

  const goToProfile = () => {
    closeSidebar();
    history.push(getSettingsRoute());
  };

  const goToInbox = () => {
    closeSidebar();
    history.push(getInboxRoute());
  };

  const onClickAddSale = () => {
    closeSidebar();
    openCreateSaleModal();
  };

  const onClickConsignToOtherSale = () => {
    closeSidebar();
    openConsignToOtherSaleModal();
  };

  const goToMoneyTable = () => {
    closeSidebar();
    history.push(getMoneyTableRoute());
  };

  const goToReportJobList = () => {
    closeSidebar();
    history.push(getReportJobList());
  };

  const goToAnalytics = () => {
    closeSidebar();
    history.push(getAnalyticsRoute());
  };

  const goToLoads = () => {
    closeSidebar();
    history.push(getLoadsRoute());
  };

  return (
    <>
      {!isMobile && (
        <SidebarListItem
          onClick={() => toggleSidebar?.()}
          icon={isOpen ? faClose : faBars}
          label={isOpen ? "Hide" : ""}
          background="transparent"
          tooltip="Show/Hide Menu"
        />
      )}
      {showSaleDashboard && (
        <SidebarListItem
          onClick={goToSaleDashboard}
          icon={faHouse}
          label="Dashboard"
        />
      )}
      {showInbox && (
        <SidebarListItem onClick={goToInbox} icon={faInbox} label="Inbox" />
      )}
      {isTransporter && (
        <SidebarListItem onClick={goToLoads} icon={faTruck} label="Loads" />
      )}
      {allowSales && (
        <SidebarListItem onClick={goToSales} icon={faGavel} label="Sales" />
      )}

      {allowAddSale && (
        <SidebarListItem
          onClick={onClickAddSale}
          icon={faPlus}
          label="Add Sale"
        />
      )}

      {allowSetNLISCredentials && saleyard && (
        <>
          {allowConsignToOtherSale && (
            <SidebarListItem
              onClick={onClickConsignToOtherSale}
              icon={faShare}
              label="Consign To"
            />
          )}
          <SidebarListItem
            onClick={goToBusinesses}
            icon={faAddressCard}
            label="Businesses"
          />
          {canSeeMultiSaleReports && (
            <SidebarListItem
              onClick={goToReportJobList}
              icon={faFileChartLine}
              label="Reports"
            />
          )}
          {canAccessAnalytics && (
            <SidebarListItem
              onClick={goToAnalytics}
              icon={faChartColumn}
              label="Analytics"
            />
          )}
          {canSeeBilling && (
            <SidebarListItem
              onClick={goToMoneyTable}
              icon={faWallet}
              label="Money"
            />
          )}

          <SidebarListItem
            onClick={goToProfile}
            icon={faCogs}
            label="Settings"
          />
        </>
      )}

      <SidebarListItem
        onClick={openHelpAndSupport}
        icon={faHeadset}
        label="Help & Support"
      />
    </>
  );
}
